import type { ComponentProps } from "react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Label, Placeholder, SemanticCOLORS } from "simplydo/react/ui";

const CardContainer = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  margin-bottom: 10px;
  max-width: 100%;
  :first-of-type {
    margin-top: 0;
  }
  :last-of-type {
    margin-bottom: 0;
  }
`;

const IdeaListCard = styled.tr`
  h4 {
    margin: 0;
  }
`;

const SelectIdeaChipStyled = styled.div<{ $compact?: boolean }>`
  display: flex;
  width: ${({ $compact }) => ($compact ? "25px" : "35px")};
  height: ${({ $compact }) => ($compact ? "40px" : "55px")};
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background-color: #e9ebee;
  color: gray;
  cursor: pointer;
  &.is-selected {
    background-color: ${({ theme }) => theme.accent};
    color: #fff;
  }
  > i {
    font-size: ${({ $compact }) => ($compact ? "1.2em" : "2em")};
    width: 1em;
    margin-right: 0;
    margin-top: ${({ $compact }) => ($compact ? "0 !important" : "7px")};
  }
  &:not(.is-selected) > i {
    font-size: 0;
  }
  &:hover {
    > i {
      font-size: ${({ $compact }) => ($compact ? "1.2em" : "2em")} !important;
    }
  }
`;

type SelectIdeaChipProps = ComponentProps<typeof SelectIdeaChipStyled> & {
  $isSelected?: boolean;
};

const SelectIdeaChip = ({
  $isSelected,
  className,
  ...props
}: SelectIdeaChipProps) => (
  <SelectIdeaChipStyled
    className={cx($isSelected && "is-selected", className)}
    {...props}
  />
);

const IdeaCoverImageStyled = styled.div<{ $coverImage: string }>`
  height: 40px;
  border-radius: 5px;
  background-image: ${({ $coverImage }) => `url(${$coverImage})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  &.is-group {
    min-width: 40px;
    margin-right: 5px;
  }
  &:not(.is-group) {
    display: flex;
    width: 100%;
  }
  &:hover {
    box-shadow: inset 0 0 0 2000px rgba(211, 211, 211, 0.3);
  }
`;

type IdeaCoverImageProps = ComponentProps<typeof IdeaCoverImageStyled> & {
  $isGroup?: boolean;
};

const IdeaCoverImage = ({
  $isGroup,
  className,
  ...props
}: IdeaCoverImageProps) => (
  <IdeaCoverImageStyled
    className={cx($isGroup && "is-group", className)}
    {...props}
  />
);

const IdeaInfoStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  &.is-group {
    padding-right: 5px;
    margin: 0;
    margin-right: 10px;
    border-right: 0.5px solid gray;
    width: 40%;
    &.is-mobile.can-manage {
      width: 30%;
    }
  }
  &:not(.is-group) {
    height: 100%;
    width: 100%;
    justify-content: center;
    padding: 5px 7.5px;
  }
  h4 {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
    margin-bottom: 2px;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .idea-authors {
    display: flex;
    color: gray;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
    .idea-others {
      cursor: pointer;
      user-select: none;
      text-decoration: underline;
      &:hover {
        color: ${({ theme }) => theme.primaryColour};
      }
    }
  }
  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 100%;
  }
`;

type IdeaInfoProps = ComponentProps<typeof IdeaInfoStyled> & {
  $isGroup?: boolean;
  $canManage?: boolean;
};

const IdeaInfo = ({
  $isGroup,
  $canManage,
  className,
  ...props
}: IdeaInfoProps) => {
  const theme = useTheme();
  return (
    <IdeaInfoStyled
      className={cx(
        $isGroup && "is-group",
        $canManage && "can-manage",
        theme.sizes.isMobile && "is-mobile",
        className,
      )}
      {...props}
    />
  );
};

type StatusLabelProps = {
  style?: React.CSSProperties;
  color?: SemanticCOLORS;
  children?: React.ReactNode;
};

const StatusLabel = ({ style, ...props }: StatusLabelProps) => (
  <Label {...props} style={{ ...style, marginRight: 5 }} circular empty />
);

const IdeaMetaStyled = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  &.is-group {
    margin: 0;
  }
  &:not(.is-group) {
    height: 100%;
    width: 100%;
    padding-left: 7.5px;
    justify-content: center;
  }
  span {
    color: gray;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
  }
`;

type IdeaMetaProps = ComponentProps<typeof IdeaMetaStyled> & {
  $isGroup?: boolean;
};

const IdeaMeta = ({ $isGroup, className, ...props }: IdeaMetaProps) => (
  <IdeaMetaStyled
    className={cx($isGroup && "is-group", className)}
    {...props}
  />
);

const IdeaUpdateStatus = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-left: 2px;
  span {
    color: black;
  }
  a {
    text-decoration: underline;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ui.label,
  i.icon {
    width: 1.3em !important;
    height: 1.3em !important;
    margin-bottom: 1.5px;
  }
`;

const IdeaActions = styled.div<{ $justify?: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $justify }) => $justify || "flex-end"};
`;

type IdeaSocialChipPlaceholderProps = { compact?: boolean };

const IdeaSocialChipPlaceholder = ({
  compact,
}: IdeaSocialChipPlaceholderProps) => (
  <Placeholder
    style={{
      height: compact ? 22 : 40,
      width: compact ? 22 : 55,
      borderRadius: 5,
      margin: "0 0 0 5px",
    }}
  >
    <Placeholder.Image square />
  </Placeholder>
);

const IdeaSocialChipStyled = styled.div<{
  $compact?: boolean;
  $fitContent?: boolean;
  $backgroundColor?: string;
}>`
  height: ${({ $compact }) => (!$compact ? "40px" : "22px")};
  width: ${({ $compact, $fitContent }) =>
    $fitContent ? "fit-content" : !$compact ? "55px" : "22px"};
  border-radius: 5px;
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor || "none"};
  margin-left: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 5px;
  justify-self: flex-end;
  cursor: pointer;
  &.is-actioned {
    background-color: ${({ theme }) => theme.accent};
  }
  &.is-warning {
    background-color: #e28024;
  }
  &.is-primary {
    background-color: #408ad4;
  }
  &:not(.hide-background):not(.is-actioned):hover {
    background-color: #e6e6e6;
  }
  &.is-actioned:not(.hide-background):hover {
    background-color: ${({ theme }) => theme.accent};
  }
  i,
  span {
    color: #505050;
  }
  &.is-actioned i,
  &.is-actioned span,
  &[data-has-bg="true"] i,
  &[data-has-bg="true"] span {
    color: white;
  }
  i {
    font-size: ${({ $compact }) => (!$compact ? "1.3em" : "1em")};
  }
  &.is-compact i {
    line-height: 1em;
  }
  i.check-mark {
    right: 6px;
    position: relative;
    top: -7px;
  }
  span {
    display: block;
    font-size: 1.4em;
    font-weight: bold;
  }
`;

type IdeaSocialChipProps = ComponentProps<typeof IdeaSocialChipStyled> & {
  $actioned?: boolean;
  $warning?: boolean;
  $primary?: boolean;
  $hideBackground?: boolean;
};

const IdeaSocialChip = ({
  $actioned,
  $warning,
  $primary,
  $hideBackground,
  $compact,
  $backgroundColor,
  className,
  ...props
}: IdeaSocialChipProps) => (
  <IdeaSocialChipStyled
    className={cx(
      $actioned && "is-actioned",
      $warning && "is-warning",
      $primary && "is-primary",
      $hideBackground && "hide-background",
      $compact && "is-compact",
      className,
    )}
    data-has-bg={$actioned || $backgroundColor ? "true" : undefined}
    $compact={$compact}
    $backgroundColor={$backgroundColor}
    {...props}
  />
);

const IdeaProjectStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 10px;
  cursor: pointer;
  span {
    color: #787878;
  }
  &.is-assigned span {
    color: #e16464;
  }
  > .lane-name {
    text-decoration: underline;
    display: block;
    margin: 0;
    font-size: 0.85em;
    max-width: 150px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

type IdeaProjectProps = ComponentProps<typeof IdeaProjectStyled> & {
  $isAssigned?: boolean;
};

const IdeaProject = ({
  $isAssigned,
  className,
  ...props
}: IdeaProjectProps) => (
  <IdeaProjectStyled
    className={cx($isAssigned && "is-assigned", className)}
    {...props}
  />
);

const AuthorPopup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AssessmentSummaryStyled = styled.span`
  text-decoration: underline;
  cursor: pointer;
  display: block;
  span {
    color: #5aa329;
  }
  &.is-incomplete span {
    color: #8e8e8e;
  }
`;

type AssessmentSummaryProps = ComponentProps<typeof AssessmentSummaryStyled> & {
  $isComplete: boolean;
};

const AssessmentSummary = ({
  $isComplete,
  className,
  ...props
}: AssessmentSummaryProps) => (
  <AssessmentSummaryStyled
    className={cx(!$isComplete && "is-incomplete", className)}
    {...props}
  />
);

export {
  CardContainer,
  SelectIdeaChip,
  IdeaListCard,
  IdeaCoverImage,
  IdeaInfo,
  StatusLabel,
  IdeaMeta,
  IdeaUpdateStatus,
  IdeaActions,
  IdeaSocialChip,
  IdeaSocialChipPlaceholder,
  AuthorPopup,
  AssessmentSummary,
  IdeaProject,
};
